(ns ui.sidebar
  "UI for the sidebar of the app."
  (:require #?(:cljs
               [parts.replicant.query :as query])
            [clojure.string :as str]
            [storrito.calendly.links :as calendly]
            [storrito.ui.core :as ui]
            [storrito.util.link :as link]
            [ui.gallery.link :as gallery-link]
            [ui.hidden-features :as hidden-features]
            [ui.icons :as icons]
            [ui.user-session :as user-session]))

(defn main-menu-items
  []
  (remove nil?
          [{:href "/ui/agenda"
            :text "Schedule"
            :svg icons/calendar}
           {:href "/ui/bulk"
            :text "Bulk Scheduling"
            :svg icons/listing}
           {:href (gallery-link/gallery-href)
            :text "Gallery"
            :svg icons/image}
           ;; Early access: the Agents page is reachable by its URL, which
           ;; the first customers get; the entry appears only with
           ;; `storrito.showHiddenFeatures()` (ui.hidden-features).
           (when (hidden-features/shown?)
             {:href "/ui/agents"
              :text "Agents"
              :badge "NEW"
              :svg icons/cpu-chip})]))

(defn option-menu-items
  [w]
  (remove nil?
  [{:href "/ui/storrito-api"
    :text "Storrito API"
    :svg icons/code-bracket}
   {:href "/ui/mcp"
    :text "Storrito MCP"
    :badge "NEW"
    :svg icons/sparkles}
   {:href "/ui/cli"
    :text "Storrito CLI"
    :badge "NEW"
    :svg icons/command-line}
   {:href "/ui/team/"
    :text "Team"
    :svg icons/team}
   {:href (if #?(:cljs (:organization/legacy-subscription-id
                         (query/get-result w {:query/kind :query/user-session}))
                 :clj  (:organization/legacy-subscription-id (:user/session w)))
            "/ui/legacy-subscription"
            "/ui/subscription?source=sidebar")
    :text "Subscription"
    :svg icons/credit-card}
   {:href "/ui/preferences"
    :text "Preferences"
    :svg icons/gear}
   {:href link/changelog-url
    :text "What's new"
    :svg icons/megaphone}
   {:href (link/help-center-url)
    :text "Help Center"
    :svg icons/question-mark-circle}]))

(def instagram-menu-items
  [{:href "/ui/instagram-connect"
    :text "Instagram Accounts"
    :svg icons/instagram}])

(def social-accounts-menu-items
  "The accounts of the platforms Storrito posts on through bundle.social
   (`app.bundle-social.platforms`)."
  [{:href "/ui/social-accounts"
    :text "Social Accounts"
    :svg icons/share}])

(def toolbox-menu-items
  [{:href "/ui/toolbox"
    :text "Free Toolbox"
    :badge "NEW"
    :svg [:svg
          {:xmlns "http://www.w3.org/2000/svg"
           :fill "none"
           :viewBox "0 0 24 24"
           :stroke-width "1.5"
           :stroke "currentColor"
           :class "w-6 h-6"}
          [:path
           {:stroke-linecap "round"
            :stroke-linejoin "round"
            :d "M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"}]]}])

(defn menu-item [{:keys [href text icon active? svg badge]}]
  [:a
   {:key text
    :class (concat ["p-2"
                    "text-left"
                    "no-underline"
                    "flex"
                    "items-center"
                    "gap-x-3"
                    "leading-6"]
                   (if active?
                     ["bg-storrito-500"
                      "text-white"
                      "hover:text-white"]
                     ["text-gray-700"
                      "hover:text-gray-900"
                      "hover:bg-gray-200"]))
    :href href
    :target (when (str/starts-with? href
                                    "http")
              "_blank")}
   (if svg
     svg
     [:i.h-6.w-6.shrink-0.text-xl.text-center
      {:class icon}])
   [:span text]
   (when badge
     [:span
      {:class ["ml-auto" "px-1" "py-px" "rounded" "text-[10px]" "font-bold"
               "uppercase" "leading-none" "bg-yellow-400" "text-gray-900"]}
      badge])])

(defn separator []
  [:div.separator
   {:style {:width "100%"
            :height 0
            :border-top "1px solid #ededed"
            :margin-top "10px"
            :margin-bottom "10px"}}])

(defn feature-request-item
  "`org-uuid` goes into the booking link so the call can be joined to
   the organization."
  [org-uuid]
  [:a.bg-green-100.relative.text-green-600
   {:class ["p-2"
            "text-left"
            "no-underline"
            "flex"
            "items-center"
            "gap-x-3"
            "leading-6"
            "text-gray-700"
            "hover:text-green-900"
            "hover:bg-green-200"]
    :href (calendly/url :feature-request "sidebar" org-uuid)
    :target "_blank"}
   icons/phone
   [:span "Share a feature request with our team!"]])

(defn href-path
  "The path of a menu link without its query string, so a link that
   carries attribution parameters (`/ui/subscription?source=sidebar`)
   still counts as active on its page."
  [href]
  (first (str/split href #"\?")))

(defn menu-item-list
  [{:keys [items active-url]}]
  (doall
    (for [{:keys [href text] :as item} items]
      ^{:key text}
      (menu-item (assoc item
                        :active?
                        (or (and (= href "#") (str/blank? active-url))
                            (= (href-path href) active-url)))))))

(defn policies []
  [:div.text-center.p-2.text-xs
   [:a.underline
    {:class ui/a-discrete
     :href "http://storrito.com/imprint/"
     :target "_blank"}
    "Imprint"]
   ", "
   [:a.underline
    {:class ui/a-discrete
     :href "http://storrito.com/terms_of_service/"
     :target "_blank"}
    " Terms of Service"]
   ", "
   [:br]
   [:a.underline
    {:class ui/a-discrete
     :href "http://storrito.com/privacy/"
     :target "_blank"}
    "Privacy Policy"]
   " and "
   [:a.underline
    {:class ui/a-discrete
     :href "http://storrito.com/cookies/"
     :target "_blank"}
    "Cookies Policy"]])

(defn spider []
  [:div.flex.justify-center.w-full
   {:x-data "{showElement: false}"
    :x-cloak "true"}
   [:a
    {:href "#"
     :style {:font-size "20px"}
     :x-show "!showElement"
     :x-on:click "showElement=true;"}
    "!"]
   [:img
    {:src "/img/spider.gif"
     :x-show "showElement"
     :x-on:click "showElement=false;"}]])

(defn separator-content [content]
  [:div.relative.flex.py-5.items-center
   [:div.flex-grow.border-t.border-gray-300]
   [:span.flex-shrink.mx-4.text-gray-300
    content]
   [:div.flex-grow.border-t.border-gray-300]])

(defn sidebar-toggle
  [state]
  [:i.fas.text-white.fixed.left-4.top-4.p-2.w-8.cursor-pointer
   {:class ["sm:left-6"
            "lg:left-8"
            (if (:ui/hide-side-menu? state)
              "fa-bars"
              "fa-xmark")
            ]
    :on {:click [[:store/assoc
                  :ui/hide-side-menu?
                  (not (:ui/hide-side-menu? state))]]}
    }
   ])

(defn sidebar
  [{:keys [ui/active-url]
    :as w}]
  [:div
   ;; hide menu on load of small screens
   {:x-data "{showElement: window.screen.width > 768}"}
   #?(:clj
      [:i.fas.text-white.fixed.left-4.top-4.p-2.w-8.cursor-pointer
       {:class ["sm:left-6"
                "lg:left-8"]
        :x-bind:class "showElement ? 'fa-xmark' : 'fa-bars';"
        :x-on:click "showElement = !showElement;"
        }])
   [:div
    {:x-show "showElement"}
    [:div.absolute.w-full.h-full.left-0.top-0.bg-black.opacity-40.visible.sm:hidden.z-10
     {:x-on:click "showElement = false;"}]
    [:aside.sm:relative.absolute.shrink-0.h-full.shadow-lg.border-r.w-60.flex.flex-col.bg-gray-50.overflow-auto.z-20


     (menu-item-list {:items (main-menu-items)
                      :active-url active-url})

     (separator-content [:i.fa-brands.fa-instagram])
     (menu-item-list {:items instagram-menu-items
                      :active-url active-url})

     (separator-content [:i.fa-solid.fa-share-nodes])
     (menu-item-list {:items social-accounts-menu-items
                      :active-url active-url})

     (separator-content [:i.fa-solid.fa-screwdriver-wrench])
     (menu-item-list {:items toolbox-menu-items
                      :active-url active-url})

     (separator)

     (feature-request-item (user-session/org-uuid w))

     [:div.flex-grow]
     (menu-item-list {:items (option-menu-items w)
                      :active-url active-url})

     (policies)]]])
